import React, { useState, useRef } from 'react';
import { VOCABULARY_BANK } from '../data/vocabularyBank';
import { isTrial } from '../data/storage';

// ── palette ──────────────────────────────────────────────────
const NAVY  = '#1a2a4a';
const GOLD  = '#b8860b';
const LGOLD = '#fef3c7';

const REF_FREE_TOPICS  = ['education', 'environment', 'health'];
const PRAC_FREE_TOPICS = ['education', 'environment'];

// ══════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

// Strip leading "to " so verb phrases match inside example sentences
function corePhrase(word) { return word.startsWith('to ') ? word.slice(3) : word; }

// Replace the core phrase inside an example sentence with "_______"
function makeBlanked(item) {
  const phrase  = corePhrase(item.word);
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const result  = item.example.replace(new RegExp(escaped, 'i'), '_______');
  return result !== item.example ? result : null;
}

// Split an example sentence at the target phrase, returning a {segments, found} object
function splitAtPhrase(item) {
  const phrase  = corePhrase(item.word);
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts   = item.example.split(new RegExp(`(${escaped})`, 'i'));
  const segments = [];
  let found = false;
  for (const p of parts) {
    if (!found && p.toLowerCase() === phrase.toLowerCase()) {
      found = true;
      segments.push({ type: 'blank', correct: item.word });
    } else if (p) {
      segments.push({ type: 'text', text: p });
    }
  }
  return { segments, found };
}

// Build a match-synonym question from an item + sibling items as distractors
function buildMatchQ(item, pool) {
  const correctSyn = item.synonym.split('/')[0].trim();
  const distractors = shuffle(pool.filter(x => x.word !== item.word))
    .slice(0, 3)
    .map(x => x.synonym.split('/')[0].trim());
  return {
    type: 'match_synonym',
    item,
    options: shuffle([correctSyn, ...distractors]),
    correct: correctSyn,
  };
}

// Generate a 10-question session for a topic
function generateQuestions(topicId) {
  const topic = VOCABULARY_BANK.topics.find(t => t.id === topicId);
  if (!topic) return [];

  const allItems = topic.sections.flatMap(s => s.items);
  const pool     = shuffle(allItems);
  const TYPES    = ['fill_blank','match_synonym','write_sentence','fill_blank','match_synonym','word_context','fill_blank','match_synonym','write_sentence','fill_blank'];
  const questions = [];
  let pi = 0;

  for (let i = 0; i < 10; i++) {
    const type = TYPES[i];
    const item = pool[pi % pool.length]; pi++;

    if (type === 'fill_blank') {
      const sentence = makeBlanked(item);
      if (!sentence) { questions.push(buildMatchQ(item, allItems)); continue; }
      const distractors = shuffle(allItems.filter(x => x.word !== item.word)).slice(0, 3).map(x => x.word);
      questions.push({ type: 'fill_blank', sentence, options: shuffle([item.word, ...distractors]), correct: item.word, explanation: item.synonym, item });

    } else if (type === 'match_synonym') {
      questions.push(buildMatchQ(item, allItems));

    } else if (type === 'write_sentence') {
      questions.push({ type: 'write_sentence', item, topicLabel: topic.label });

    } else if (type === 'word_context') {
      // Pick 3 items from the same section; each shows its example with ONE blank dropdown
      const section   = topic.sections.find(s => s.items.some(x => x.word === item.word));
      const srcItems  = section ? shuffle(section.items) : shuffle(allItems);
      const blankData = srcItems
        .slice(0, 6)
        .map(si => {
          const { segments, found } = splitAtPhrase(si);
          if (!found) return null;
          const opts = shuffle([si.word, ...shuffle(allItems.filter(x => x.word !== si.word)).slice(0, 3).map(x => x.word)]);
          return { item: si, segments, options: opts, correct: si.word };
        })
        .filter(Boolean)
        .slice(0, 3);

      if (blankData.length < 2) { questions.push(buildMatchQ(item, allItems)); continue; }
      questions.push({ type: 'word_context', blankData, sectionLabel: section?.title || '' });
    }
  }
  return questions;
}

// ══════════════════════════════════════════════════════════════
// SMALL SHARED COMPONENTS
// ══════════════════════════════════════════════════════════════

// Highlight query text inside a string
function Hl({ text, q }) {
  if (!q?.trim()) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: LGOLD, color: '#92400e', padding: '0 1px', borderRadius: 2 }}>
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

// Word card in the reference tab
function WordCard({ item, q, color }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(item.word).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => setCopied(true));
  }
  return (
    <div style={{ background: '#fff', border: '1px solid #e8ecf2', borderRadius: 10, padding: '12px 14px', position: 'relative', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <button onClick={copy}
        style={{ position: 'absolute', top: 9, right: 9, background: copied ? '#f0faf4' : '#f7f9fc', border: `1px solid ${copied ? '#bbf7d0' : '#e2e6ed'}`, borderRadius: 6, padding: '2px 8px', fontSize: 10.5, cursor: 'pointer', color: copied ? '#15803d' : '#5a6a88', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 600 }}>
        {copied ? '✓ Copied!' : 'Copy'}
      </button>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, paddingRight: 56, lineHeight: 1.3 }}>
        <Hl text={item.word} q={q} />
      </div>
      <div style={{ fontSize: 12, color: GOLD, fontWeight: 500 }}>
        ≈ <Hl text={item.synonym} q={q} />
      </div>
      <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic', lineHeight: 1.55 }}>
        <Hl text={item.example} q={q} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// REFERENCE TAB
// ══════════════════════════════════════════════════════════════

function ReferenceTab({ trial }) {
  const [query,        setQuery]        = useState('');
  const [topicFilter,  setTopicFilter]  = useState('all');
  const [openSections, setOpenSections] = useState({});

  const topics = VOCABULARY_BANK.topics;
  const visible = topicFilter === 'all' ? topics : topics.filter(t => t.id === topicFilter);

  function toggle(key) { setOpenSections(p => ({ ...p, [key]: !p[key] })); }

  function matches(item) {
    if (!query.trim()) return true;
    const lq = query.toLowerCase();
    return item.word.toLowerCase().includes(lq) || item.synonym.toLowerCase().includes(lq) || item.example.toLowerCase().includes(lq);
  }

  const pill = (active, onClick, children, disabled) => (
    <button onClick={onClick} disabled={disabled}
      style={{ border: `1.5px solid ${active ? GOLD : '#dde3ed'}`, background: active ? LGOLD : '#fff', color: disabled ? '#c0c0c0' : active ? '#92400e' : '#5a6a88', borderRadius: 100, padding: '5px 14px', fontSize: 12.5, cursor: disabled ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: active ? 600 : 400, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'all .12s' }}>
      {children}
    </button>
  );

  return (
    <div>
      <style>{`.vb-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}@media(max-width:599px){.vb-grid{grid-template-columns:1fr}}`}</style>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: '#9aa3b8', pointerEvents: 'none' }}>🔍</span>
        <input value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search words, synonyms, or examples…"
          style={{ width: '100%', padding: '10px 36px 10px 36px', border: '1.5px solid #dde3ed', borderRadius: 9, fontSize: 13.5, fontFamily: "'Plus Jakarta Sans',sans-serif", color: NAVY, outline: 'none', boxSizing: 'border-box' }}
        />
        {query && (
          <button onClick={() => setQuery('')}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#9aa3b8', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
        )}
      </div>

      {/* Topic pills */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 20 }}>
        {pill(topicFilter === 'all', () => setTopicFilter('all'), 'All Topics', false)}
        {topics.map(t => {
          const locked = trial && !REF_FREE_TOPICS.includes(t.id);
          return pill(
            topicFilter === t.id,
            () => { if (!locked) setTopicFilter(t.id); },
            <>{t.icon} {t.label}{locked && ' 🔒'}</>,
            locked
          );
        })}
      </div>

      {/* Topics */}
      {visible.map(topic => {
        const locked = trial && !REF_FREE_TOPICS.includes(topic.id);

        const filteredSections = topic.sections.map(s => ({
          ...s, items: query.trim() ? s.items.filter(matches) : s.items,
        })).filter(s => s.items.length > 0);

        if (query.trim() && filteredSections.length === 0) return null;
        const displaySections = query.trim() ? filteredSections : topic.sections;

        return (
          <div key={topic.id} style={{ marginBottom: 22, borderRadius: 12, border: '1px solid #e2e6ed', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ background: topic.bgColor, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{topic.icon}</span>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 700, color: topic.color, flex: 1 }}>{topic.label}</div>
              {locked && <span style={{ fontSize: 11.5, color: '#9aa3b8', background: '#fff', border: '1px solid #dde3ed', borderRadius: 100, padding: '3px 10px', flexShrink: 0 }}>🔒 Standard / Premium</span>}
            </div>

            {locked ? (
              <div style={{ padding: '22px 18px', textAlign: 'center', color: '#9aa3b8', fontSize: 13 }}>
                Upgrade to Standard or Premium to unlock {topic.label} vocabulary.
              </div>
            ) : (
              <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {displaySections.map((sec, si) => {
                  const key  = `${topic.id}_${si}`;
                  const open = openSections[key] !== false; // default open
                  return (
                    <div key={si} style={{ border: '1px solid #e8ecf2', borderRadius: 9, overflow: 'hidden' }}>
                      <button onClick={() => toggle(key)}
                        style={{ width: '100%', background: '#f7f9fc', border: 'none', padding: '9px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif' " }}>
                        <span style={{ fontWeight: 700, color: topic.color, fontSize: 12.5 }}>{sec.title}</span>
                        <span style={{ color: '#9aa3b8', fontSize: 12 }}>{open ? '▲' : '▼'} {sec.items.length}</span>
                      </button>
                      {open && (
                        <div className="vb-grid" style={{ padding: '10px 12px' }}>
                          {sec.items.map((item, ii) => (
                            <WordCard key={ii} item={item} q={query} color={topic.color} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {query.trim() && visible.every(t => {
        if (trial && !REF_FREE_TOPICS.includes(t.id)) return true;
        return t.sections.every(s => s.items.filter(matches).length === 0);
      }) && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9aa3b8', fontSize: 13.5 }}>
          No matches for "<strong>{query}</strong>". Try a different search term.
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PRACTICE EXERCISES
// ══════════════════════════════════════════════════════════════

function ExLabel({ label, color = GOLD }) {
  return (
    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color, marginBottom: 12, fontWeight: 700 }}>
      {label}
    </div>
  );
}

function NextBtn({ onClick, children = 'Next Question →' }) {
  return (
    <button onClick={onClick}
      style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", marginTop: 14 }}>
      {children}
    </button>
  );
}

// ── Exercise 1: Fill in the Blank ────────────────────────────
function FillBlank({ q, onNext }) {
  const [selected, setSelected] = useState(null);
  const isCorrect = selected === q.correct;

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e6ed', padding: '22px 20px' }}>
      <ExLabel label="Fill in the blank" />
      <div style={{ fontSize: 15, color: NAVY, lineHeight: 1.85, marginBottom: 20, fontFamily: "'Times New Roman',serif", background: '#f7f9fc', borderRadius: 8, padding: '12px 16px' }}>
        {q.sentence}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 9 }}>
        {q.options.map((opt, i) => {
          const isSelected = selected === opt;
          const right = selected && opt === q.correct;
          const wrong = isSelected && !right;
          return (
            <button key={i} onClick={() => { if (!selected) setSelected(opt); }}
              style={{ padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${right ? '#16a34a' : wrong ? '#dc2626' : '#dde3ed'}`, background: right ? '#f0faf4' : wrong ? '#fff5f5' : '#fff', fontSize: 13, cursor: selected ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", color: right ? '#14532d' : wrong ? '#991b1b' : NAVY, textAlign: 'left', lineHeight: 1.4, transition: 'all .12s' }}>
              {right && '✓ '}{wrong && '✗ '}{opt}
            </button>
          );
        })}
      </div>
      {selected && (
        <>
          <div style={{ marginTop: 14, background: isCorrect ? '#f0faf4' : '#fff5f5', border: `1px solid ${isCorrect ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: isCorrect ? '#14532d' : '#991b1b' }}>
            {isCorrect ? '✓ Correct!' : `✗ The answer was: ${q.correct}`}
            <div style={{ fontSize: 11.5, color: '#5a6a88', marginTop: 4 }}>≈ {q.explanation}</div>
          </div>
          <NextBtn onClick={() => onNext(isCorrect, 'fill_blank')} />
        </>
      )}
    </div>
  );
}

// ── Exercise 2: Match the Synonym ────────────────────────────
function MatchSynonym({ q, onNext }) {
  const [selected, setSelected] = useState(null);
  const isCorrect = selected === q.correct;

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e6ed', padding: '22px 20px' }}>
      <ExLabel label="Match the synonym" />
      <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 8, lineHeight: 1.3 }}>{q.item.word}</div>
      <div style={{ fontSize: 12, color: '#5a6a88', marginBottom: 20 }}>Select the best synonym for this vocabulary item:</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {q.options.map((opt, i) => {
          const isSelected = selected === opt;
          const right = selected && opt === q.correct;
          const wrong = isSelected && !right;
          return (
            <button key={i} onClick={() => { if (!selected) setSelected(opt); }}
              style={{ padding: '11px 16px', borderRadius: 8, border: `1.5px solid ${right ? '#16a34a' : wrong ? '#dc2626' : '#dde3ed'}`, background: right ? '#f0faf4' : wrong ? '#fff5f5' : '#fff', fontSize: 13.5, cursor: selected ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", color: right ? '#14532d' : wrong ? '#991b1b' : NAVY, textAlign: 'left', transition: 'all .12s', display: 'flex', alignItems: 'center', gap: 8 }}>
              {right && <span>✓</span>}{wrong && <span>✗</span>}{opt}
            </button>
          );
        })}
      </div>
      {selected && (
        <>
          <div style={{ marginTop: 14, background: isCorrect ? '#f0faf4' : '#fff5f5', border: `1px solid ${isCorrect ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: isCorrect ? '#14532d' : '#991b1b' }}>
            {isCorrect ? '✓ Correct!' : `✗ Best answer: ${q.correct}`}
            <div style={{ fontSize: 11.5, color: '#5a6a88', marginTop: 3 }}>Full synonym list: {q.item.synonym}</div>
          </div>
          <NextBtn onClick={() => onNext(isCorrect, 'match_synonym')} />
        </>
      )}
    </div>
  );
}

// ── Exercise 3: Write a Sentence ─────────────────────────────
function WriteSentence({ q, onNext }) {
  const [text,     setText]     = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  async function getFeedback() {
    if (!text.trim()) return;
    setLoading(true); setError(''); setFeedback('');
    try {
      const res = await fetch('/api/anthropic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5',
          max_tokens: 250,
          system: 'You are an expert IELTS examiner. A student is practising using academic vocabulary. Assess their sentence in exactly 3 lines: Line 1: Did they use the target word/phrase correctly? (Yes/No + one reason). Line 2: One specific grammar or vocabulary improvement. Line 3: A Band 7 model sentence using the same target word. Maximum 80 words total. Be encouraging.',
          messages: [{ role: 'user', content: `Target word/phrase: "${q.item.word}"\nTopic: ${q.topicLabel}\nStudent sentence: "${text}"\n\nAssess in exactly 3 lines.` }],
        }),
      });
      const data = await res.json();
      setFeedback(data.content?.[0]?.text || '');
    } catch (e) { setError('Could not get feedback: ' + e.message); }
    setLoading(false);
  }

  const lines = feedback.trim().split('\n').filter(Boolean);

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e6ed', padding: '22px 20px' }}>
      <ExLabel label="Write a sentence" />
      <div style={{ background: LGOLD, border: '1px solid #fde68a', borderRadius: 8, padding: '11px 14px', marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: '#92400e', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Target phrase — {q.topicLabel}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{q.item.word}</div>
        <div style={{ fontSize: 12, color: '#b45309', marginTop: 3 }}>≈ {q.item.synonym}</div>
      </div>
      <div style={{ fontSize: 12.5, color: '#5a6a88', marginBottom: 8 }}>Use this phrase in a sentence about {q.topicLabel.toLowerCase()}:</div>
      <textarea value={text} onChange={e => setText(e.target.value)}
        placeholder={`Write a sentence using "${q.item.word}"…`}
        rows={3}
        style={{ width: '100%', border: '1.5px solid #dde3ed', borderRadius: 8, padding: '11px 13px', fontSize: 14, fontFamily: "'Times New Roman',serif", lineHeight: 1.75, color: NAVY, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: feedback ? 14 : 0 }}>
        <button onClick={getFeedback} disabled={loading || !text.trim()}
          style={{ background: (!text.trim() || loading) ? '#d1d5db' : NAVY, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: (!text.trim() || loading) ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          {loading ? '⏳ Checking…' : '🤖 Get AI Feedback'}
        </button>
        <button onClick={() => onNext(null, 'write_sentence')}
          style={{ background: 'none', border: '1px solid #dde3ed', color: '#5a6a88', borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          Skip →
        </button>
      </div>
      {error && <div style={{ color: '#dc2626', fontSize: 12.5, marginTop: 8 }}>{error}</div>}
      {feedback && (
        <div style={{ background: '#f7f9fc', border: '1px solid #dde3ed', borderRadius: 10, overflow: 'hidden', marginTop: 14 }}>
          <div style={{ background: NAVY, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, color: '#fff', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>🤖 Examiner Feedback</div>
          <div style={{ padding: '13px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lines.map((line, i) => {
              const colors = [
                { bg: '#f0faf4', border: '#bbf7d0', color: '#14532d' },
                { bg: '#fff8f0', border: '#fde8cc', color: '#7c2d12' },
                { bg: '#eff6ff', border: '#bfdbfe', color: '#1e3a8a' },
              ];
              const c = colors[i] || colors[2];
              return (
                <div key={i} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 7, padding: '8px 11px', fontSize: 12.5, color: c.color, lineHeight: 1.55 }}>
                  {line.replace(/^(Line\s*\d+:\s*)/i, '')}
                </div>
              );
            })}
          </div>
          <div style={{ padding: '0 14px 12px' }}>
            <NextBtn onClick={() => onNext(null, 'write_sentence')}>Next Question →</NextBtn>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Exercise 4: Word in Context (dropdown gap-fill) ──────────
function WordContext({ q, onNext }) {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);

  const allAnswered = q.blankData.every((_, i) => answers[i] !== undefined && answers[i] !== '');
  const numCorrect  = q.blankData.filter((b, i) => answers[i] === b.correct).length;
  const allCorrect  = numCorrect === q.blankData.length;

  function setAns(i, v) { if (!checked) setAnswers(p => ({ ...p, [i]: v })); }

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e6ed', padding: '22px 20px' }}>
      <ExLabel label="Word in context — select the correct word for each blank" />
      {q.sectionLabel && <div style={{ fontSize: 11.5, color: '#9aa3b8', marginBottom: 14, fontFamily: "'JetBrains Mono',monospace" }}>Category: {q.sectionLabel}</div>}

      {q.blankData.map((b, i) => {
        const val        = answers[i];
        const isCorrect  = checked && val === b.correct;
        const isWrong    = checked && val && val !== b.correct;
        const borderCol  = checked ? (isCorrect ? '#16a34a' : isWrong ? '#dc2626' : '#dde3ed') : '#e2e6ed';
        const bgCol      = checked ? (isCorrect ? '#f0faf4' : isWrong ? '#fff5f5' : '#fff') : '#fafafa';

        return (
          <div key={i} style={{ marginBottom: 12, padding: '12px 14px', background: bgCol, border: `1.5px solid ${borderCol}`, borderRadius: 9, fontSize: 14, lineHeight: 2, fontFamily: "'Times New Roman',serif", color: NAVY, transition: 'all .2s' }}>
            {b.segments.map((seg, si) => {
              if (seg.type === 'text') return <span key={si}>{seg.text}</span>;
              return (
                <select key={si} value={val || ''} onChange={e => setAns(i, e.target.value)}
                  disabled={checked}
                  style={{ border: `2px solid ${checked ? (isCorrect ? '#16a34a' : '#dc2626') : GOLD}`, background: checked ? (isCorrect ? '#f0faf4' : '#fff5f5') : LGOLD, borderRadius: 6, padding: '2px 8px', fontSize: 12.5, fontFamily: "'Plus Jakarta Sans',sans-serif", cursor: checked ? 'default' : 'pointer', margin: '0 3px', maxWidth: 220 }}>
                  <option value="">— select —</option>
                  {b.options.map((opt, oi) => <option key={oi} value={opt}>{opt}</option>)}
                </select>
              );
            })}
            {checked && (
              <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12, marginLeft: 8, fontStyle: 'normal' }}>
                {isCorrect ? <span style={{ color: '#15803d' }}>✓</span> : <span style={{ color: '#dc2626' }}>✗ {b.correct}</span>}
              </span>
            )}
          </div>
        );
      })}

      {!checked ? (
        <button onClick={() => allAnswered && setChecked(true)} disabled={!allAnswered}
          style={{ background: allAnswered ? NAVY : '#d1d5db', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 13, fontWeight: 600, cursor: allAnswered ? 'pointer' : 'default', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          Check Answers
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 14, color: allCorrect ? '#15803d' : NAVY, fontWeight: 600 }}>
            {numCorrect}/{q.blankData.length} correct {allCorrect ? '🎉' : ''}
          </div>
          <NextBtn onClick={() => onNext(allCorrect, 'word_context')} />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PRACTICE TAB
// ══════════════════════════════════════════════════════════════

function PracticeTab({ trial }) {
  const allowedTopics = trial
    ? VOCABULARY_BANK.topics.filter(t => PRAC_FREE_TOPICS.includes(t.id))
    : VOCABULARY_BANK.topics;

  const initTopic = allowedTopics[0]?.id || 'education';

  const [topicId,    setTopicId]    = useState(initTopic);
  const [questions,  setQuestions]  = useState(() => generateQuestions(initTopic));
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score,      setScore]      = useState({ correct: 0, total: 0 });

  function startSession(id) {
    setTopicId(id);
    setQuestions(generateQuestions(id));
    setCurrentIdx(0);
    setScore({ correct: 0, total: 0 });
  }

  function handleNext(wasCorrect, qType) {
    if (qType !== 'write_sentence' && wasCorrect !== undefined) {
      setScore(p => ({ correct: p.correct + (wasCorrect ? 1 : 0), total: p.total + 1 }));
    }
    setCurrentIdx(p => p + 1);
  }

  const topic = VOCABULARY_BANK.topics.find(t => t.id === topicId);

  // Session complete
  if (currentIdx >= questions.length) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 20px' }}>
        <div style={{ fontSize: 48, marginBottom: 14 }}>🎓</div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 700, color: NAVY, marginBottom: 8 }}>Session Complete!</div>
        <div style={{ fontSize: 15, color: '#5a6a88', marginBottom: 6 }}>
          Score: <strong style={{ color: score.correct === score.total && score.total > 0 ? '#15803d' : GOLD }}>{score.correct}/{score.total}</strong> correct
        </div>
        <div style={{ fontSize: 13, color: '#9aa3b8', marginBottom: 28 }}>Topic: {topic?.label}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => startSession(topicId)}
            style={{ background: GOLD, color: '#fff', border: 'none', borderRadius: 8, padding: '11px 24px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            New Session
          </button>
          <button onClick={() => startSession(allowedTopics[(allowedTopics.findIndex(t => t.id === topicId) + 1) % allowedTopics.length]?.id || initTopic)}
            style={{ background: '#f7f9fc', border: '1px solid #dde3ed', color: NAVY, borderRadius: 8, padding: '11px 24px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Change Topic
          </button>
        </div>
      </div>
    );
  }

  const q = questions[currentIdx];

  return (
    <div>
      {/* Session header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        {/* Topic selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: '#5a6a88', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Topic:</span>
          <select value={topicId} onChange={e => startSession(e.target.value)}
            style={{ border: '1.5px solid #dde3ed', borderRadius: 7, padding: '5px 10px', fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif", color: NAVY, background: '#fff', cursor: 'pointer', outline: 'none' }}>
            {allowedTopics.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
          </select>
        </div>
        {/* Score */}
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#5a6a88', background: '#f7f9fc', border: '1px solid #dde3ed', borderRadius: 7, padding: '5px 12px' }}>
          Session score: <strong style={{ color: NAVY }}>{score.correct}/{score.total}</strong>
        </div>
      </div>

      {/* Progress dots */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 18, alignItems: 'center' }}>
        {questions.map((_, i) => (
          <div key={i} style={{ width: i === currentIdx ? 18 : 8, height: 8, borderRadius: 4, background: i < currentIdx ? GOLD : i === currentIdx ? NAVY : '#e2e6ed', transition: 'all .25s' }} />
        ))}
        <span style={{ fontSize: 11, color: '#9aa3b8', marginLeft: 6, fontFamily: "'JetBrains Mono',monospace" }}>{currentIdx + 1}/10</span>
      </div>

      {/* Exercise */}
      <div key={currentIdx}>
        {q.type === 'fill_blank'    && <FillBlank    q={q} onNext={handleNext} />}
        {q.type === 'match_synonym' && <MatchSynonym  q={q} onNext={handleNext} />}
        {q.type === 'write_sentence'&& <WriteSentence q={q} onNext={handleNext} />}
        {q.type === 'word_context'  && <WordContext    q={q} onNext={handleNext} />}
      </div>

      {/* Trial note */}
      {trial && (
        <div style={{ marginTop: 18, padding: '9px 14px', background: '#fff8f0', border: '1px solid #fde8cc', borderRadius: 8, fontSize: 12, color: '#92400e', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          🔒 Trial: practise Education and Environment. Upgrade to access all 10 topics.
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════

export default function VocabularyBank({ candidate }) {
  const [tab, setTab] = useState('reference');
  const trial = isTrial(candidate);

  const tabBtn = (id, label) => (
    <button key={id} onClick={() => setTab(id)}
      style={{ border: 'none', borderBottom: `2.5px solid ${tab === id ? GOLD : 'transparent'}`, background: 'none', color: tab === id ? GOLD : '#5a6a88', padding: '10px 22px', fontSize: 13.5, fontWeight: tab === id ? 600 : 500, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all .15s', whiteSpace: 'nowrap' }}>
      {label}
    </button>
  );

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: NAVY, marginBottom: 4 }}>
          Vocabulary Bank
        </div>
        <div style={{ fontSize: 13, color: '#5a6a88', lineHeight: 1.5 }}>
          280+ IELTS academic words and phrases across 10 topics — with synonyms, examples, and practice exercises.
          {trial && <span style={{ marginLeft: 8, background: '#fff8f0', border: '1px solid #fde8cc', color: '#b8620a', borderRadius: 100, padding: '1px 10px', fontSize: 11.5, fontWeight: 600 }}>Free trial: 3 reference topics · 2 practice topics</span>}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #dde3ed', marginBottom: 22 }}>
        {tabBtn('reference', '📚 Reference')}
        {tabBtn('practice',  '✏️ Practice')}
      </div>

      {tab === 'reference' && <ReferenceTab trial={trial} />}
      {tab === 'practice'  && <PracticeTab  trial={trial} />}
    </div>
  );
}
